import React, { useEffect, useRef, useState } from "react";
import { BsX, BsLink45Deg, BsCheck2, BsImage, BsDownload } from "react-icons/bs";
import { FaXTwitter, FaTelegram, FaLinkedinIn, FaWhatsapp } from "react-icons/fa6";
import { MdEmail } from "react-icons/md";
import useOutsideClick from "../../hooks/useOutsideClick";
import notify from "./notify";

/**
 * ShareModal - Professional sharing component with preview
 * 
 * @param {Object} props
 * @param {boolean} props.show - Show/hide modal
 * @param {function} props.setShow - Toggle modal visibility
 * @param {string} props.type - Type of content: 'project' | 'community' | 'result'
 * @param {Object} props.data - Content data object
 * @param {string} props.data.title - Title of the content
 * @param {string} props.data.description - Short description
 * @param {string} props.data.image - Image URL
 * @param {string} props.data.creator - Creator name
 * @param {string} props.data.category - Category label
 * @param {Object} props.data.extra - Extra data (price, goal, etc.)
 * @param {string} props.data.id - Content ID for badge generation
 */
const ShareModal = ({
  show,
  setShow,
  type = "project",
  data = {},
}) => {
  const modalRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  
  // Generate badge URL for social sharing
  const backendUrl = process.env.REACT_APP_BASE_URL || "https://api.fundesci.org";
  const badgeUrl = data.id 
    ? `${backendUrl}/badges/${type}/${data.id}?format=png`
    : null;

  const handleClose = () => {
    setShow(false);
    setCopied(false);
    setSelectedPlatform(null);
  };

  useOutsideClick(modalRef, handleClose, null, show);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [show]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  
  // Helper to strip HTML tags from text
  const stripHtml = (html) => {
    if (!html) return "";
    // Remove HTML tags
    const text = html.replace(/<[^>]*>/g, " ");
    // Decode HTML entities
    const decoded = text
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
    // Clean up extra whitespace
    return decoded.replace(/\s+/g, " ").trim();
  };

  // Generate professional share text based on content type
  const generateShareText = (platform) => {
    const { title, description, creator, extra } = data;
    const cleanDescription = stripHtml(description);
    const hashtags = "#DeSci #FunDeSci #Science #Web3";
    
    if (type === "community") {
      const price = extra?.price || "0";
      const currency = extra?.currency || "ETH";
      const members = extra?.members || 0;
      
      switch (platform) {
        case "twitter":
          return `🔬 Discover "${title}" on @FunDeSci!\n\n💎 Membership NFT: ${price} ${currency}\n👥 ${members} members already joined\n\n🚀 Join the DeSci revolution and unlock exclusive perks!\n\n${hashtags}`;
        case "linkedin":
          return `I'm excited to share this DeSci community: "${title}"\n\n${cleanDescription?.slice(0, 200)}${cleanDescription?.length > 200 ? "..." : ""}\n\nCreated by ${creator}\n\n🔬 Join the decentralized science movement on FunDeSci!`;
        case "telegram":
        case "whatsapp":
          return `🔬 Check out "${title}" on FunDeSci!\n\n${cleanDescription?.slice(0, 150)}${cleanDescription?.length > 150 ? "..." : ""}\n\n💎 Get your membership NFT for ${price} ${currency}\n👥 ${members} members\n\n🚀 Join now:`;
        case "email":
          return `Hi,\n\nI wanted to share this amazing DeSci community with you: "${title}"\n\n${cleanDescription}\n\nMembership is available for ${price} ${currency}.\n\nCheck it out here: ${shareUrl}\n\nBest regards`;
        default:
          return `${title} - ${cleanDescription?.slice(0, 100)}`;
      }
    } else if (type === "project") {
      const goal = extra?.goal || "0";
      const currency = extra?.currency || "USD";
      const raised = extra?.raised || 0;
      const method = extra?.method || "Crowdfunding";
      
      switch (platform) {
        case "twitter":
          return `🧬 Support groundbreaking research: "${title}" on @FunDeSci!\n\n💰 Goal: $${goal}\n📊 Already raised: $${raised}\n🔬 Method: ${method}\n\n🚀 Help fund the future of science!\n\n${hashtags}`;
        case "linkedin":
          return `I'm supporting this innovative research project: "${title}"\n\n${cleanDescription?.slice(0, 250)}${cleanDescription?.length > 250 ? "..." : ""}\n\nFunding Goal: $${goal} ${currency}\nCreated by: ${creator}\n\n🔬 Decentralized Science (DeSci) is changing how research gets funded. Learn more on FunDeSci!`;
        case "telegram":
        case "whatsapp":
          return `🧬 Support this research project!\n\n"${title}"\n\n${cleanDescription?.slice(0, 150)}${cleanDescription?.length > 150 ? "..." : ""}\n\n💰 Goal: $${goal}\n📊 Raised: $${raised}\n\n🚀 Fund the future of science:`;
        case "email":
          return `Hi,\n\nI wanted to share this exciting research project with you: "${title}"\n\n${cleanDescription}\n\nFunding Goal: $${goal} ${currency}\nProgress: $${raised} raised\n\nYou can support this project here: ${shareUrl}\n\nBest regards`;
        default:
          return `${title} - ${cleanDescription?.slice(0, 100)}`;
      }
    } else {
      // Result or other type
      switch (platform) {
        case "twitter":
          return `📄 New research result published on @FunDeSci!\n\n"${title}"\n\nBy ${creator}\n\n🔬 Open science, powered by Web3\n\n${hashtags}`;
        default:
          return `${title} - ${cleanDescription?.slice(0, 100)}`;
      }
    }
  };

  const platforms = [
    {
      id: "twitter",
      name: "X (Twitter)",
      icon: FaXTwitter,
      color: "bg-black hover:bg-gray-800",
      textColor: "text-white",
      getUrl: () => {
        const text = generateShareText("twitter");
        // Twitter Card will use the badge image via meta tags
        return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
      },
    },
    {
      id: "linkedin",
      name: "LinkedIn",
      icon: FaLinkedinIn,
      color: "bg-[#0A66C2] hover:bg-[#004182]",
      textColor: "text-white",
      getUrl: () => {
        // LinkedIn will use Open Graph meta tags with badge image
        return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
      },
    },
    {
      id: "telegram",
      name: "Telegram",
      icon: FaTelegram,
      color: "bg-[#229ED9] hover:bg-[#1a7eb0]",
      textColor: "text-white",
      getUrl: () => {
        const text = generateShareText("telegram");
        return `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`;
      },
    },
    {
      id: "whatsapp",
      name: "WhatsApp",
      icon: FaWhatsapp,
      color: "bg-[#25D366] hover:bg-[#1da851]",
      textColor: "text-white",
      getUrl: () => {
        const text = generateShareText("whatsapp");
        return `https://wa.me/?text=${encodeURIComponent(text + "\n\n" + shareUrl)}`;
      },
    },
    {
      id: "email",
      name: "Email",
      icon: MdEmail,
      color: "bg-gray-600 hover:bg-gray-700",
      textColor: "text-white",
      getUrl: () => {
        const subject = `Check out this ${type} on FunDeSci: ${data.title}`;
        const body = generateShareText("email");
        return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      },
    },
  ];

  const handleShare = (platform) => {
    setSelectedPlatform(platform.id);
    window.open(platform.getUrl(), "_blank", "width=600,height=500,scrollbars=yes");
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      notify("success", "Link copied to clipboard!");
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      notify("error", "Failed to copy link");
    }
  };

  const handleDownloadBadge = async () => {
    if (!badgeUrl) {
      notify("error", "Badge not available");
      return;
    }

    try {
      // For PNG format
      const response = await fetch(badgeUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-${data.id}-badge.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      notify("success", "Badge downloaded!");
    } catch (err) {
      notify("error", "Failed to download badge");
    }
  };

  if (!show) return null;

  const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black/60 backdrop-blur-sm z-50 p-4">
      <div
        ref={modalRef}
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-800">Share {typeLabel}</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <BsX className="text-2xl text-gray-500" />
          </button>
        </div>

        {/* Preview Card */}
        <div className="px-6 py-4">
          <p className="text-xs text-gray-500 mb-3 font-medium uppercase tracking-wide">
            Preview
          </p>
          <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
            {/* Badge Image Preview */}
            <div className="relative h-40 bg-gradient-to-br from-purple-100 to-blue-100">
              {badgeUrl ? (
                <img
                  src={badgeUrl}
                  alt={`${data.title} badge`}
                  className="w-full h-full object-contain p-2"
                  onError={(e) => {
                    // Fallback to regular image on error
                    e.target.src = data.image || "";
                    e.target.className = "w-full h-full object-cover";
                  }}
                />
              ) : data.image ? (
                <img
                  src={data.image}
                  alt={data.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <BsImage className="text-4xl text-gray-300" />
                </div>
              )}
              <div className="absolute top-3 left-3">
                <span className="px-2 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-gray-700 capitalize">
                  {type}
                </span>
              </div>
              {badgeUrl && (
                <div className="absolute bottom-3 right-3">
                  <span className="px-2 py-1 bg-primary/90 backdrop-blur-sm rounded-full text-xs font-medium text-white">
                    Badge Image
                  </span>
                </div>
              )}
            </div>
            
            {/* Content Preview */}
            <div className="p-4">
              <p className="text-xs text-gray-400 mb-1">fundesci.com</p>
              <h3 className="font-semibold text-gray-800 line-clamp-2 mb-1">
                {data.title || "Untitled"}
              </h3>
              <p className="text-sm text-gray-500 line-clamp-2">
                {stripHtml(data.description) || "No description available"}
              </p>
              {data.creator && (
                <p className="text-xs text-gray-400 mt-2">
                  By {data.creator}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Share Platforms */}
        <div className="px-6 pb-4">
          <p className="text-xs text-gray-500 mb-3 font-medium uppercase tracking-wide">
            Share on
          </p>
          <div className="grid grid-cols-5 gap-2">
            {platforms.map((platform) => {
              const Icon = platform.icon;
              return (
                <button
                  key={platform.id}
                  onClick={() => handleShare(platform)}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all ${platform.color} ${platform.textColor} group`}
                  title={platform.name}
                >
                  <Icon className="text-xl" />
                  <span className="text-[10px] font-medium truncate w-full text-center">
                    {platform.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Copy Link Section */}
        <div className="px-6 pb-6">
          <p className="text-xs text-gray-500 mb-3 font-medium uppercase tracking-wide">
            Or copy link
          
          {/* Download Badge Button */}
          {badgeUrl && (
            <div className="mt-3">
              <button
                onClick={handleDownloadBadge}
                className="w-full px-4 py-3 rounded-xl font-medium bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:opacity-90 transition-all flex items-center justify-center gap-2"
              >
                <BsDownload />
                Download Badge Image
              </button>
            </div>
          )}
          </p>
          <div className="flex gap-2">
            <div className="flex-1 flex items-center bg-gray-100 rounded-xl px-4 py-3 overflow-hidden">
              <BsLink45Deg className="text-gray-400 text-lg flex-shrink-0 mr-2" />
              <span className="text-sm text-gray-600 truncate">{shareUrl}</span>
            </div>
            <button
              onClick={handleCopyLink}
              className={`px-4 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
                copied
                  ? "bg-green-500 text-white"
                  : "bg-primary text-white hover:bg-primary/90"
              }`}
            >
              {copied ? (
                <>
                  <BsCheck2 />
                  Copied
                </>
              ) : (
                "Copy"
              )}
            </button>
          </div>
        </div>

        {/* Message Preview for Selected Platform */}
        {selectedPlatform && (
          <div className="px-6 pb-6">
            <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">
              Message Preview
            </p>
            <div className="bg-gray-50 rounded-xl p-4 max-h-32 overflow-y-auto">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {generateShareText(selectedPlatform)}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareModal;
